import { css, html, LitElement } from 'lit'
import { property } from 'lit/decorators.js'
import type { AvatarArgs } from '@/components/Avatar/types'
import { safeCustomElement } from '@/utilities/custom-element'
import type { AvatarListItemSize } from './types'

@safeCustomElement('mdc-avatar-list-item')
export class MdcAvatarListItem extends LitElement {
  @property({ type: String }) size: AvatarListItemSize = 'md'
  @property({ type: Object }) avatar: Omit<
    AvatarArgs,
    'size' | 'title' | 'subtitle' | 'nameVariant'
  > & {
    title: string
    subtitle?: string
    nameVariant?: AvatarArgs['nameVariant']
  } = {
    title: '',
  }

  static styles = css`
		:host {
			display: block;
			width: 100%;
			/* CSS Custom Properties for customization */
			--mdc-avatar-list-item-padding-xs: var(--spacing-025, 0.25rem) var(--spacing-050, 0.5rem);
			--mdc-avatar-list-item-padding-sm: var(--spacing-050, 0.5rem);
			--mdc-avatar-list-item-padding-md: var(--spacing-075, 0.75rem) var(--spacing-050, 0.5rem);
			--mdc-avatar-list-item-padding-lg: var(--spacing-100, 1rem) var(--spacing-075, 0.75rem);
			--mdc-avatar-list-item-border-color: var(--color-functional-gray-75);
			--mdc-avatar-list-item-hover-background: var(--color-functional-gray-25);
			--mdc-avatar-list-item-transition-duration: 0.2s;
			font-family: var(--typography-font-family-sans);
		}

		.mdc-avatar-list-item {
			display: flex;
			flex-direction: column;
			align-items: flex-start;
			width: 100%;
			transition: background-color var(--mdc-avatar-list-item-transition-duration);
			border-bottom: 1px solid var(--mdc-avatar-list-item-border-color);
		}

		.mdc-avatar-list-item:hover {
			background-color: var(--mdc-avatar-list-item-hover-background);
		}

		.mdc-avatar-list-item--size-md {
			padding: var(--mdc-avatar-list-item-padding-md);
		}

		.mdc-avatar-list-item--size-sm {
			padding: var(--mdc-avatar-list-item-padding-sm);
		}

		.mdc-avatar-list-item--size-xs {
			padding: var(--mdc-avatar-list-item-padding-xs);
		}

		.mdc-avatar-list-item--size-lg {
			padding: var(--mdc-avatar-list-item-padding-lg);
		}
	`

  private get avatarSize(): AvatarArgs['size'] {
    // Map list item size to avatar size
    const sizeMap: Record<AvatarListItemSize, AvatarArgs['size']> = {
      xs: 'xs',
      sm: 'sm',
      md: 'md',
      lg: 'lg',
    }
    return sizeMap[this.size] || 'md'
  }

  render() {
    const className = `mdc-avatar-list-item mdc-avatar-list-item--size-${this.size}`

    return html`
			<div class=${className}>
				<mdc-avatar
					size=${this.avatarSize}
					src=${this.avatar.src || ''}
					alt=${this.avatar.alt || ''}
					text=${this.avatar.text || ''}
					?isMore=${this.avatar.isMore || false}
					title=${this.avatar.title}
					subtitle=${this.avatar.subtitle || ''}
					nameVariant=${this.avatar.nameVariant || 'bold'}
				></mdc-avatar>
			</div>
		`
  }
}
